import { ApiError, endpoints } from '@gopinhos/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LanguageSwitcher } from '../components/ui/language-switcher';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { apiClient } from '../lib/api-client';

export function ActivatePage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const tokenValid = token.length >= 20;

  return (
    <div className="relative flex min-h-full items-center justify-center bg-surface-50 px-4 py-10">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-xl">{t('activate.title')}</CardTitle>
          <p className="text-sm text-surface-500">{t('activate.subtitle')}</p>
        </CardHeader>
        <CardContent>
          {!tokenValid ? (
            <p className="text-sm text-red-600">{t('activate.invalidToken')}</p>
          ) : done ? (
            <div className="space-y-3">
              <p className="text-sm text-emerald-700">{t('activate.success')}</p>
              <Button className="w-full" onClick={() => navigate('/login')}>
                {t('common.signIn')}
              </Button>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                setError(null);
                if (password.length < 12) {
                  setError(t('activate.passwordTooShort'));
                  return;
                }
                if (password !== confirm) {
                  setError(t('activate.passwordsDontMatch'));
                  return;
                }
                setSubmitting(true);
                try {
                  await apiClient.request({
                    method: 'POST',
                    path: endpoints.userActivate(),
                    body: { token, password },
                  });
                  setDone(true);
                } catch (err) {
                  setError(
                    err instanceof ApiError ? err.problem.title : t('activate.errorGeneric'),
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="password">{t('activate.newPassword')}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="text-xs text-surface-500">{t('activate.passwordHint')}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">{t('activate.confirmPassword')}</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                {submitting ? t('common.creating') : t('activate.submit')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
